import { Fragment, type ReactNode } from 'react';
import {
  type DietaryRestrictionsText,
  getWarningText,
} from '@hungryroot/dietary-restrictions';
import { type OrderIngredient } from '@hungryroot/orders';
import { WarningCallout, WarningCalloutVariant } from '@hungryroot/pho';
import { Paragraph } from '@hungryroot/ramen';
import {
  PointsWithIcon,
  PointsWithIconVariant,
} from '../PointsWithIcon/PointsWithIcon';
import styles from './Ingredient.module.css';

export enum IngredientVariant {
  Removed,
  Swap,
  Default,
}

export enum PointsPlacementVariant {
  Edit,
  Swap,
}

export interface IngredientViewModel
  extends Pick<
    OrderIngredient,
    | 'id'
    | 'name'
    | 'packaged_image_url'
    | 'card_image_url'
    | 'brand_name'
    | 'amount'
    | 'points'
    | 'discount'
    | 'points_after_discount'
    | 'discount_percentage'
  > {
  dietaryIngredients?: DietaryRestrictionsText[];
  containsNeverItem?: boolean;
}

export interface OrderIngredientViewModel extends OrderIngredient {
  dietaryIngredients?: DietaryRestrictionsText[];
  containsNeverItem?: boolean;
}

interface RemovedVariantProps {
  variant: IngredientVariant.Removed;
  actions?: never;
  selected?: never;
  onSelect?: never;
}

interface DefaultVariantProps {
  variant?: IngredientVariant.Default;
  actions?: ReactNode | null;
  selected?: never;
  onSelect?: never;
}

interface SwapVariantProps {
  variant: IngredientVariant.Swap;
  actions?: never;
  selected?: boolean;
  onSelect?: () => void;
}

type Props = {
  testid?: string;
  ingredient: IngredientViewModel;
  isModalEditor?: boolean;
  pointsPlacementVariant?: PointsPlacementVariant | null;
  className?: string;
} & (RemovedVariantProps | DefaultVariantProps | SwapVariantProps);

function getContainerClassName(props: Props) {
  switch (props.variant) {
    case IngredientVariant.Removed:
      return styles.removed;
    case IngredientVariant.Swap: {
      if (props.selected === true) {
        return styles.selected;
      }
      return styles.swappable;
    }
    case undefined:
    case IngredientVariant.Default:
      return props.isModalEditor
        ? styles['item-container-modal-editor']
        : styles['item-container'];
  }
}

function getDetailedWarningText(
  dietaryIngredients: DietaryRestrictionsText[],
  containsNeverItem?: boolean
) {
  const containsDietaryIngredients = dietaryIngredients.length > 0;
  if (containsDietaryIngredients) {
    return `Contains ${getWarningText(dietaryIngredients)}`;
  } else if (containsNeverItem) {
    return `Set to 'never'`;
  }
  return null;
}

export function Ingredient(props: Props) {
  const {
    ingredient,
    testid,
    variant = IngredientVariant.Default,
    pointsPlacementVariant,
    className,
  } = props;
  const {
    packaged_image_url,
    name: ingredientName,
    brand_name,
    discount,
    points_after_discount,
    points,
    amount,
    dietaryIngredients = [],
    containsNeverItem,
  } = ingredient;
  const containerClassName = getContainerClassName(props);
  const showQuantity =
    variant === IngredientVariant.Default && amount > 1 && !props.actions;

  const warningText = getDetailedWarningText(
    dietaryIngredients,
    containsNeverItem
  );

  const pointsContainer = (
    <PointsWithIcon
      discount={discount}
      pointsAfterDiscount={points_after_discount}
      discountedPriceFirst={
        pointsPlacementVariant === PointsPlacementVariant.Edit
      }
      points={points.toString()}
      variant={PointsWithIconVariant.Large}
      testid={`${testid}-points`}
      ariaLabel="ingredient-points"
      className={
        pointsPlacementVariant === PointsPlacementVariant.Edit
          ? styles['points-bold']
          : undefined
      }
    />
  );

  const body = (
    <Fragment key="ingredient-body">
      {packaged_image_url !== null && (
        <div className={styles['container-img']}>
          <img
            data-testid={`${testid}-image`}
            className={styles['thumbnail-img']}
            src={packaged_image_url}
            alt="food-img"
          />
        </div>
      )}
      <div className={styles['name-container']}>
        <Paragraph testid={`${testid}-brand-name`} variant="caption">
          {brand_name}
        </Paragraph>
        <div className={styles['title-container']}>
          <Paragraph testid={`${testid}-name`} variant="body">
            {ingredientName}
          </Paragraph>
          {showQuantity && (
            <Paragraph
              variant="body"
              className={styles['ingredient-quantity']}
              testid={`${testid}-quantity`}
            >
              x{amount}
            </Paragraph>
          )}
        </div>
        {pointsPlacementVariant === PointsPlacementVariant.Edit &&
          pointsContainer}
        {warningText && variant !== IngredientVariant.Removed && (
          <WarningCallout
            testid={`${testid}-ingredient-warning`}
            text={warningText}
            variant={
              dietaryIngredients.length > 0
                ? WarningCalloutVariant.Restrictions
                : WarningCalloutVariant.Never
            }
          />
        )}
      </div>
      {variant === IngredientVariant.Removed && (
        <Paragraph variant="caption" className={styles['removed-text']}>
          Removed
        </Paragraph>
      )}
      {variant === IngredientVariant.Default && props.actions}
      {pointsPlacementVariant === PointsPlacementVariant.Swap &&
        pointsContainer}
    </Fragment>
  );

  if (variant !== IngredientVariant.Swap) {
    return (
      <div className={containerClassName} data-testid={`${testid}`}>
        {body}
      </div>
    );
  }

  return (
    <button
      onClick={props.onSelect}
      className={`${containerClassName} ${className}`}
      data-testid={`${testid}`}
    >
      {body}
    </button>
  );
}
